import { AlertCircle, Code2, Copy, Loader2, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { useWidgetSettings } from "@/hooks/use-widget-settings";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type ExistingWidget = { id: number; name: string; publicKey: string };

export function WidgetSettings() {
  const {
    canManageWidgets,
    title,
    setTitle,
    allowedDomains,
    setAllowedDomains,
    generatedKey,
    error,
    isLoading,
    widgets,
    createDisabled,
    isCreating,
    handleSubmit,
    handleDelete,
    deletingWidgetId,
  } = useWidgetSettings();
  const [copied, setCopied] = useState<"snippet" | null>(null);
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [expandedWidgetId, setExpandedWidgetId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ExistingWidget | null>(null);

  useEffect(() => {
    if (generatedKey) {
      setIsCreateFormOpen(false);
    }
  }, [generatedKey]);

  const widgetScriptUrl = useMemo(
    () => (typeof window !== "undefined" ? `${window.location.origin}/widget.js` : "/widget.js"),
    [],
  );
  const buildScriptSnippet = (widgetKey: string) => (
    `<script src="${widgetScriptUrl}" data-widget-key="${widgetKey}"></script>`
  );
  const generatedScriptSnippet = generatedKey ? buildScriptSnippet(generatedKey) : "";

  const copyText = async (value: string, type: "snippet") => {
    if (!value || typeof navigator === "undefined" || !navigator.clipboard) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopied(type);
      window.setTimeout(() => setCopied((prev) => (prev === type ? null : prev)), 1500);
    } catch {
      // Clipboard permissions can be denied depending on browser/security context.
      // Swallow errors so the settings screen remains usable.
    }
  };

  if (!canManageWidgets) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Only owners and admins can create embeds.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4 max-sm:flex-col max-sm:items-stretch max-sm:gap-3">
            <div>
              <CardTitle>Create Embed</CardTitle>
              <CardDescription>
                Add an embed name and allowed domains, then generate a ready-to-copy snippet.
              </CardDescription>
            </div>
            <Button
              type="button"
              onClick={() => setIsCreateFormOpen((prev) => !prev)}
              aria-expanded={isCreateFormOpen}
              aria-controls="create-embed-form"
              className="max-sm:w-full"
            >
              {isCreateFormOpen ? "Hide form" : "New embed"}
            </Button>
          </div>
        </CardHeader>
        {isCreateFormOpen && (
          <CardContent id="create-embed-form">
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="widget-title">Embed name</Label>
                <Input
                  id="widget-title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  maxLength={120}
                  placeholder="Website Claim Form"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="widget-domains">Allowed domains (one per line)</Label>
                <Textarea
                  id="widget-domains"
                  value={allowedDomains}
                  onChange={(event) => setAllowedDomains(event.target.value)}
                  rows={4}
                  placeholder={"https://example.com\nhttps://www.example.com"}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Required. Add every origin the widget may be embedded on, including https://
                </p>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" disabled={createDisabled} className="max-sm:w-full">
                {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create embed
              </Button>
            </form>
          </CardContent>
        )}
      </Card>

      {generatedKey && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Code2 className="h-4 w-4" /> Embed Code
            </CardTitle>
            <CardDescription>Copy the script snippet below.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Textarea
                id="generated-embed-snippet"
                value={generatedScriptSnippet}
                readOnly
                rows={8}
                aria-label="Generated embed snippet"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => copyText(generatedScriptSnippet, "snippet")}
                className="max-sm:w-full"
              >
                <Copy className="h-4 w-4 mr-1.5" />
                {copied === "snippet" ? "Copied" : "Copy snippet"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Existing Embeds</CardTitle>
          <CardDescription>Recently created embeds for this company.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <div className="flex items-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading embeds...
            </div>
          ) : widgets.length === 0 ? (
            <p className="text-sm text-muted-foreground">No embeds yet.</p>
          ) : (
            widgets.map((widget: ExistingWidget) => (
              <div key={widget.id} className="rounded-md border">
                <div className="p-3 flex items-center justify-between gap-3">
                  <p className="text-sm font-medium">{widget.name}</p>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setExpandedWidgetId((prev) => (prev === widget.id ? null : widget.id))}
                    >
                      {expandedWidgetId === widget.id ? "Hide" : "View"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label={`Delete ${widget.name}`}
                      disabled={deletingWidgetId === widget.id}
                      onClick={() => setDeleteTarget(widget)}
                    >
                      {deletingWidgetId === widget.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 text-destructive" />
                      )}
                    </Button>
                  </div>
                </div>

                {expandedWidgetId === widget.id && (
                  <div className="border-t p-3">
                    <div className="space-y-2">
                      <Textarea
                        value={buildScriptSnippet(widget.publicKey)}
                        readOnly
                        rows={4}
                        aria-label={`${widget.name} embed snippet`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => copyText(buildScriptSnippet(widget.publicKey), "snippet")}
                        className="max-sm:w-full"
                      >
                        <Copy className="h-4 w-4 mr-1.5" />
                        {copied === "snippet" ? "Copied" : "Copy snippet"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete embed?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `Delete embed "${deleteTarget.name}"? Sites using this snippet will stop working.`
                : "Delete this embed? Sites using this snippet will stop working."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!deleteTarget) return;
                handleDelete(deleteTarget.id);
                setDeleteTarget(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}